<script setup lang="ts">
    import { computed, onMounted, watch, shallowReactive, type Ref } from 'vue'
    import { useRoute } from 'vue-router'

    import { Refresh } from '@browser/Browser'
    import { generateName } from '@common/Common'
    import PortfolioNavigation from '@common/Components/PortfolioNavigation.vue'
    import { guid } from '@common/Guid'

    import PortfolioScenarioEditor from '@Quest/PortfolioScenarios/Components/PortfolioScenarioEditor.vue'
    import { PortfolioScenarioEditorViewModel } from '@Quest/PortfolioScenarios/Components/PortfolioScenarioEditor'

    import { usePortfolioScenariosPageQueryV1 } from '@Quest/PortfolioScenarios/queries-vue-composables'

    import
        {
            useClonePortfolioScenarioCommandV1,
            useConnectToCorporateScenarioCommandV1,
            useDeletePortfolioScenarioCommandV1
        } from '@Quest/PortfolioScenarios/commands-vue-composables'

    import { useExportAllActiveProjectPlansCommandV1 } from '@Quest/Portfolios/commands-vue-composables'

    import type {
        CorporateScenarioReadModel,
        PortfolioScenarioReadModel,
        PortfolioScenariosPageReadModel
    } from '@Quest/PortfolioScenarios/readmodels'

    import moment from 'moment'


    // ======================
    // Route
    // ======================

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const tchProjectId = computed(() => Number(route.params.tchProjectId ?? 0))


    // ======================
    // Query
    // ======================

    const {
        runPortfolioScenariosPageQueryV1,
        modelPortfolioScenariosPageQueryV1
    } = usePortfolioScenariosPageQueryV1(workspaceId, tchProjectId)

    const rm = modelPortfolioScenariosPageQueryV1 as Ref<PortfolioScenariosPageReadModel | null>
    const portfolioId = computed(() => rm.value?.PortfolioId)

    const {
        invokeClonePortfolioScenarioCommandV1,
        onSucceedClonePortfolioScenarioCommandV1
    } = useClonePortfolioScenarioCommandV1(workspaceId, portfolioId)

    const {
        invokeConnectToCorporateScenarioCommandV1,
        onSucceedConnectToCorporateScenarioCommandV1
    } = useConnectToCorporateScenarioCommandV1(workspaceId, portfolioId)

    const {
        invokeDeletePortfolioScenarioCommandV1,
        onSucceedDeletePortfolioScenarioCommandV1
    } = useDeletePortfolioScenarioCommandV1(workspaceId, portfolioId)

    const {
        invokeExportAllActiveProjectPlansCommandV1,
        onSucceedExportAllActiveProjectPlansCommandV1,
        result: resultExportAllActiveProjectPlansCommandV1
    } = useExportAllActiveProjectPlansCommandV1(workspaceId, portfolioId)


    const portfolioScenarioEditor = shallowReactive(new PortfolioScenarioEditorViewModel())


    // ======================
    // Formatting
    // ======================

    function formatDate(date: Date | null)
    {
        if (!date) return null
        return moment(date).format('MMM D, YYYY')
    }

    function formatTime(date: Date | null)
    {
        if (!date) return null
        return moment(date).format('LT')
    }


    // ======================
    // Actions
    // ======================

    function showPortfolioScenarioCreator(cScenarioId: string)
    {
        portfolioScenarioEditor.showCreator(
            workspaceId.value,
            portfolioId.value,
            cScenarioId
        )
    }

    function showPortfolioScenarioEditor(pScenario: PortfolioScenarioReadModel)
    {
        portfolioScenarioEditor.showEditor(
            workspaceId.value,
            portfolioId.value,
            pScenario.Id
        )
    }

    function handleConnectToCorporateScenarioCommand(
        pScenario: PortfolioScenarioReadModel,
        cScenario: CorporateScenarioReadModel
    )
    {
        invokeConnectToCorporateScenarioCommandV1({
            Id: pScenario.Id,
            CorporateScenarioId: cScenario.Id
        })
    }

    function handleClonePortfolioScenarioCommand(s: PortfolioScenarioReadModel)
    {
        invokeClonePortfolioScenarioCommandV1({
            SourceId: s.Id,
            Id: guid(),
            CloneName: generateName(s.Name)
        })
    }

    function handleDeletePortfolioScenarioCommand(scenarioId: string)
    {
        if (confirm('Are you sure?') === true)
        {
            invokeDeletePortfolioScenarioCommandV1({ Id: scenarioId })
        }
    }

    function handleExportAllActiveProjectPlansCommand()
    {
        invokeExportAllActiveProjectPlansCommandV1()
    }

    function onScenarioCreated()
    {
        Refresh()
    }

    function onScenarioUpdated()
    {
        Refresh()
    }

    onMounted(() =>
    {
        document.title = 'Portfolio Plans'
        runPortfolioScenariosPageQueryV1()
    })

    watch(rm, (model) =>
    {
        if (!model) return

        if (model.RedirectUrl)
        {
            location.href = model.RedirectUrl
        }
    })


    onSucceedClonePortfolioScenarioCommandV1(() =>
    {
        Refresh()
    })

    onSucceedDeletePortfolioScenarioCommandV1(() =>
    {
        Refresh()
    })

    onSucceedConnectToCorporateScenarioCommandV1(() =>
    {
        Refresh()
    })

    onSucceedExportAllActiveProjectPlansCommandV1(() =>
    {
        location.href = resultExportAllActiveProjectPlansCommandV1.value
    })


</script>

<template>
    <div>
        <PortfolioScenarioEditor :viewModel="portfolioScenarioEditor" 
                                 @scenario-created="onScenarioCreated" 
                                 @scenario-updated="onScenarioUpdated" />

        <PortfolioNavigation :workspaceId="workspaceId" :projectId="tchProjectId" />

        <div v-if="rm" class="container" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Planning</h1>
                </div>
                <div class="col-lg-6 text-right align-self-end">
                    <a class="btn btn-outline-success mr-1" style="padding: 0.7em 2em;" v-sanitizedHref="`/#/enterprise/planning/${rm.StrategyWorkspaceId}/${rm.StrategyId}`">Enterprise plans</a>

                    <a class="btn btn-lg btn-outline-secondary" href="https://www.transparentchoice.com/client-hub/user-guide/scenarios" title="Documentation" target="_blank">
                        <i class="fa fa-info" aria-hidden="true"></i>
                    </a>

                    <!--<div class="dropdown d-inline">
                        <button class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown" style="padding: 0.7em 2em;">
                            Actions
                        </button>
                        <div class="dropdown-menu dropdown-menu-right dropdown-menu-scrollable" role="menu">
                            <a class="dropdown-item" v-sanitizedHref="`/#/portfolio/import/${workspaceId}/${tchProjectId}`">Import business cases</a>
                            <a class="dropdown-item" @click="handleExportAllActiveProjectPlansCommand">Export business cases</a>
                        </div>
                    </div>-->

                </div>
            </div>

            <hr style="border-color: #bbb; margin: 20px 0 60px;" />

            <table class="table table-bordered table-hover mb-3" v-if="rm.NotConnectedScenarios.length">
                <tbody>
                    <tr v-for="s in rm.NotConnectedScenarios" :key="s.Id" class="table-danger">
                        <td>
                            {{s.Name}}<br />
                            <small class="text-muted">Created: {{formatDate(s.CreatedDate)}}, {{formatTime(s.CreatedDate)}} by {{s.CreatedByName}}</small>
                        </td>
                        <td class="text-center align-middle" style="width: 150px;">
                            <div class="btn-group">
                                <button type="button" class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                                    Connect
                                </button>
                                <div class="dropdown-menu dropdown-menu-right">
                                    <a v-for="cs in rm.CorporateScenarios" :key="cs.Id" class="dropdown-item" @click="handleConnectToCorporateScenarioCommand(s, cs)">
                                        {{cs.Name}}
                                    </a>
                                </div>
                            </div>
                        </td>
                        <td class="text-center align-middle" style="width: 70px;">
                            <button class="btn btn-outline-secondary" @click="handleDeletePortfolioScenarioCommand(s.Id)">
                                <i class="fa-regular fa-trash-can"></i>
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
            
            <table class="table table-bordered table-hover" v-for="cs in rm.CorporateScenarios" :key="cs.Id">
                <tbody>
                    <tr class="table-secondary">
                        <td style="width: 60px;" >
                            <button class="btn btn-outline-secondary" @click="cs.IsCollapsed = !cs.IsCollapsed">
                                <i :class="['fa', cs.IsCollapsed ? 'fa-chevron-down' : 'fa-chevron-up']"></i>
                            </button>
                        </td>
                        <td>
                            <span class="badge badge-primary float-right">Enterprise plan</span>

                            <small class="text-muted">{{formatDate(cs.StartDate)}} - {{formatDate(cs.EndDate)}}</small><br />
                            <a class="text-primary" v-sanitizedHref="`/#/enterprise-plan/portfolios/${rm.StrategyWorkspaceId}/${rm.StrategyId}/${cs.Id}`">
                                {{cs.Name}}
                            </a>
                            <div v-if="cs.IsCollapsed === false">
                                <small>{{cs.Description}}</small>
                            </div>
                        </td>
                    </tr>
                    <tr v-for="ps in cs.Scenarios" :key="ps.Id" v-if="cs.IsCollapsed === false">
                        <td>
                            <div class="dropdown">
                                <button type="button" class="btn btn-outline-secondary" data-toggle="dropdown">
                                    <i class="fa-solid fa-ellipsis"></i>
                                </button>
                                <div class="dropdown-menu">
                                    <a class="dropdown-item" v-sanitizedHref="`/#/portfolio-plan/projects/${workspaceId}/${tchProjectId}/${ps.Id}`">View</a>
                                    <a class="dropdown-item" @click="showPortfolioScenarioEditor(ps)">Settings</a>
                                    <a class="dropdown-item" @click="handleClonePortfolioScenarioCommand(ps)">Clone</a>
                                    <div class="dropdown-divider"></div>
                                    <a class="dropdown-item" @click="handleDeletePortfolioScenarioCommand(ps.Id)">Delete</a>
                                </div>
                            </div>
                        </td>
                        <td>
                            <span class="badge badge-secondary float-right">Portfolio plan</span>
                            <a class="text-primary" v-sanitizedHref="`/#/portfolio-plan/projects/${workspaceId}/${tchProjectId}/${ps.Id}`">
                                {{ps.Name}}
                            </a><br />
                            <small class="text-muted">Created: {{formatDate(ps.CreatedDate)}}, {{formatTime(ps.CreatedDate)}} by {{ps.CreatedByName}}</small>
                            <div>
                                <small>{{ps.Description}}</small>
                            </div>
                        </td>
                    </tr>
                    <tr v-if="cs.IsCollapsed === false">
                        <td colspan="2" class="text-center">
                            <button type="button" class="btn btn-secondary" @click="showPortfolioScenarioCreator(cs.Id)">
                                Create portfolio plan
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>


